import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CreditCard, X, DollarSign, CheckCircle2, ShieldCheck, AlertCircle } from "lucide-react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import { AdminEnrollmentItem } from "../../../services/enrollmentService";
import { useUpdateAdminEnrollmentMutation } from "../../../hooks/queries/useEnrollmentAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface ConfirmTuitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  enrollment: AdminEnrollmentItem | null;
  onSuccess: () => void;
}

const ConfirmTuitionModal: React.FC<ConfirmTuitionModalProps> = ({
  isOpen,
  onClose,
  enrollment,
  onSuccess,
}) => {
  const { addToast } = useToast();

  const [amount, setAmount] = useState<number | "">(
    enrollment?.payment_amount ||
      (enrollment?.delivery_mode === "online"
        ? enrollment?.curriculum?.online_price || 0
        : enrollment?.curriculum?.onsite_price || 0)
  );
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [activateAccess, setActivateAccess] = useState(true);

  const { mutateAsync: updateEnrollmentMutation, isPending: isSubmitting } =
    useUpdateAdminEnrollmentMutation();

  if (!isOpen || !enrollment) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateEnrollmentMutation({
        id: enrollment.id,
        mode: enrollment.delivery_mode,
        data: {
          is_paid: true,
          is_enrolled: activateAccess ? true : enrollment.is_enrolled,
          payment_amount: Number(amount) || 0,
          payment_method: paymentMethod,
        },
      });

      addToast("Tuition payment recorded and confirmed successfully!", "success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to confirm tuition payment:", err);
      const errorMsg = err?.response?.data?.detail || "Failed to settle tuition payment.";
      addToast(errorMsg, "error");
    }
  };

  return (
    <AnimatePresence>
      <div className="cht-fixed cht-inset-0 cht-z-50 cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-black/50 cht-backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="cht-w-full cht-max-w-lg cht-bg-white cht-rounded-3xl cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden"
        >
          {/* Header */}
          <div className="cht-px-6 cht-py-5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-bg-gray-50/50">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-emerald-50 cht-text-emerald-600 cht-flex cht-items-center cht-justify-center">
                <CreditCard size={20} />
              </div>
              <div>
                <h3 className="cht-text-base cht-font-black cht-text-gray-900 cht-tracking-tight">
                  Record Tuition Clearance
                </h3>
                <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
                  Confirm off-platform payment and activate student curriculum access
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="cht-p-2 cht-text-gray-400 hover:cht-text-gray-600 cht-rounded-full hover:cht-bg-gray-100 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="cht-p-6 cht-space-y-5">
            {/* Student & Curriculum Overview Card */}
            <div className="cht-p-4 cht-rounded-2xl cht-bg-gray-50 cht-border cht-border-gray-100 cht-space-y-2">
              <div className="cht-flex cht-items-center cht-justify-between">
                <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Student
                </span>
                <span className="cht-text-xs cht-font-bold cht-text-gray-900">
                  {enrollment.student_details?.name || enrollment.student_details?.email}
                </span>
              </div>
              <div className="cht-flex cht-items-center cht-justify-between">
                <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Curriculum
                </span>
                <span className="cht-text-xs cht-font-bold cht-text-[#3e57da]">
                  {enrollment.curriculum?.title}
                </span>
              </div>
              <div className="cht-flex cht-items-center cht-justify-between">
                <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  Format
                </span>
                <span
                  className={`cht-text-[10px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-full ${
                    enrollment.delivery_mode === "online"
                      ? "cht-bg-blue-50 cht-text-blue-700"
                      : "cht-bg-emerald-50 cht-text-emerald-700"
                  }`}
                >
                  {enrollment.delivery_mode}
                </span>
              </div>
            </div>

            {/* Amount Input */}
            <Input
              label="Settled Tuition Amount (₦) *"
              type="number"
              placeholder="e.g. 50000"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || "")}
              icon={<DollarSign size={15} />}
            />

            {/* Payment Method Selector */}
            <div className="cht-space-y-1.5">
              <label className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                Payment Channel / Source
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="cht-w-full cht-px-4 cht-py-3 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-text-xs cht-font-medium cht-text-gray-800 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 focus:cht-border-[#3e57da]"
              >
                <option value="Bank Transfer">Bank Transfer (Direct / Wire)</option>
                <option value="POS Terminal">POS Terminal (Campus Physical)</option>
                <option value="Cash Deposit">Cash Deposit</option>
                <option value="Paystack Manual">Paystack Manual Reconcile</option>
                <option value="Scholarship">Full Scholarship / Waiver</option>
                <option value="Admin Override">Admin Manual Override</option>
              </select>
            </div>

            {/* Immediate Access Toggle */}
            <label className="cht-p-3 cht-rounded-xl cht-border cht-border-gray-200 cht-flex cht-items-center cht-gap-3 cht-cursor-pointer hover:cht-bg-gray-50">
              <input
                type="checkbox"
                checked={activateAccess}
                onChange={(e) => setActivateAccess(e.target.checked)}
                className="cht-w-4 cht-h-4 cht-rounded cht-text-emerald-600 focus:cht-ring-emerald-500"
              />
              <div>
                <p className="cht-text-xs cht-font-bold cht-text-gray-800">
                  Ensure Active Curriculum Access
                </p>
                <p className="cht-text-[10px] cht-text-gray-400">
                  Instantly unlocks full modules, videos, and exercises for student
                </p>
              </div>
            </label>

            {/* Actions */}
            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                className="cht-rounded-xl cht-text-xs cht-font-bold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-rounded-xl cht-text-xs cht-font-bold cht-px-6"
              >
                {isSubmitting ? "Settling..." : "Confirm & Settle Tuition"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfirmTuitionModal;
